import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Flame, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE, NAV_LINKS } from "@/data/site";
import { scrollToEl } from "@/hooks/useLenis";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    scrollToEl(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <Flame className="w-6 h-6 text-ember group-hover:text-gold transition-colors duration-300" />
          <span className="font-display text-xl md:text-2xl text-white tracking-wide">
            {SITE.ticker}
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-body text-sm font-semibold text-gray-300 hover:text-gold transition-colors duration-300 uppercase tracking-wide"
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/giveaway"
            data-testid="nav-link-giveaway"
            className="inline-flex items-center gap-1.5 font-body text-sm font-extrabold text-black bg-gradient-to-r from-gold to-ember px-4 py-2 rounded-full uppercase tracking-wide hover:scale-105 transition-transform duration-300"
          >
            <Gift className="w-4 h-4" /> Giveaway
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={SITE.links.pumpfun}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-buy-button"
            className="hidden sm:inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-black font-body font-extrabold text-sm px-5 py-2.5 rounded-md transition-colors duration-300 glow-gold uppercase tracking-wide"
          >
            <Flame className="w-4 h-4" /> Buy {SITE.ticker}
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-4 gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className="text-left font-body font-semibold text-gray-200 hover:text-gold py-3 border-b border-white/5 uppercase tracking-wide transition-colors duration-300"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/giveaway"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-giveaway"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold to-ember text-black font-extrabold py-3 rounded-md uppercase tracking-wide"
              >
                <Gift className="w-4 h-4" /> Giveaway
              </Link>
              <a
                href={SITE.links.pumpfun}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-gold text-black font-extrabold py-3 rounded-md uppercase tracking-wide"
              >
                <Flame className="w-4 h-4" /> Buy {SITE.ticker}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
